import BillingService from "@/src/api/BillingService.js";
import BillingServiceV4 from "@/src/api/BillingServiceV4.js";
import DashboardService from "@/src/api/DashboardService.js";
import {
  billingAccountStatus,
  billingPacks,
  billingVersions,
} from "@/src/constants/billingConstants.js";
import { toCalculateDue } from "@/src/utils/billingUtils.js";

export default {
  namespaced: true,

  state: {
    billingVersion: billingVersions.V1,
    hasPaidPlan: false,
    humanSeats: 0,
    status: "",
    userSeatEnabled: false,
    hasCardAttched: false,
    setupBilling: {
      monthly: {
        starter: {
          seatPrice: 0,
          credits: 0,
        },
        growth: {
          seatPrice: 0,
          credits: 0,
        },
        pro: {
          seatPrice: 0,
          credits: 0,
        },
      },
      quarterly: {
        starter: {
          seatPrice: 0,
          credits: 0,
        },
        growth: {
          seatPrice: 0,
          credits: 0,
        },
        pro: {
          seatPrice: 0,
          credits: 0,
        },
      },
      semi_annual: {
        starter: {
          seatPrice: 0,
          credits: 0,
        },
        growth: {
          seatPrice: 0,
          credits: 0,
        },
        pro: {
          seatPrice: 0,
          credits: 0,
        },
      },
      annual: {
        starter: {
          seatPrice: 0,
          credits: 0,
        },
        growth: {
          seatPrice: 0,
          credits: 0,
        },
        pro: {
          seatPrice: 0,
          credits: 0,
        },
      },
      two_year: {
        starter: {
          seatPrice: 0,
          credits: 0,
        },
        growth: {
          seatPrice: 0,
          credits: 0,
        },
        pro: {
          seatPrice: 0,
          credits: 0,
        },
      },
      selectedPlan: "growth",
      userSeatCount: 1,
      trialDays: 0,
      freePlanCredits: 0,
      isBillingSetupComplete: false,
      billingPack: "monthly",
      currentPlanId: null,
    },
    paymentDue: false,
    retryLink: "",
    overdueSince: 0,
    billingEndDate: null,
    enabledBillingPeriods: {
      monthly: true,
      quarterly: true,
      semi_annual: true,
      annual: true,
      two_year: true,
    },
    enabledTiers: ["custom", "pro", "growth", "free"], //"starter",
    isV3: false,
    isV4: false,
    // v4 uses a wallet model: credits purchased and accumulated in additional_credits table
    v4: {
      additionalCredits: 0, // running wallet balance (accumulates across purchases)
      isRecurring: false,   // whether the active purchase is a recurring subscription
    },
  },

  mutations: {
    SET_BILLING_VERSION(state, value) {
      state.billingVersion = value;
    },

    SET_HAS_PAID_PLAN(state, value) {
      state.hasPaidPlan = value;
    },

    SET_HAS_CARD_ATTCHED(state, value) {
      state.hasCardAttched = value;
    },

    SET_IS_V3(state, value) {
      state.isV3 = value;
    },

    SET_IS_V4(state, value) {
      state.isV4 = value;
    },

    // value: { additional_credits: number, is_recurring: boolean }
    SET_V4_CREDITS(state, value) {
      state.v4.additionalCredits = value.additional_credits ?? 0;
      state.v4.isRecurring = value.is_recurring ?? false;
    },

    SET_HUMAN_SEATS(state, value) {
      state.humanSeats = value;
    },

    SET_ACCOUNT_STATUS(state, value) {
      state.status = value;
    },

    SET_USER_SEAT_ENABLED(state, value) {
      state.userSeatEnabled = value;
    },

    SET_SELECTED_PLAN(state, value) {
      state.setupBilling.selectedPlan = value;
    },

    SET_USER_SEAT_COUNT(state, value) {
      state.setupBilling.userSeatCount = value;
    },

    SET_IS_BILLING_SETUP_COMPLETE(state, value) {
      state.setupBilling.isBillingSetupComplete = value;
    },

    SET_PLANS(state, value) {
      if (value && value.length > 0) {
        const plan = value[0];

        // Monthly
        state.setupBilling.monthly.starter.seatPrice =
          plan.starter_seat_price || 0;
        state.setupBilling.monthly.starter.credits =
          plan.starter_credits_per_seat || 0;
        state.setupBilling.monthly.growth.seatPrice =
          plan.human_seat_price || 0;
        state.setupBilling.monthly.growth.credits =
          plan.software_credits_per_human_seat || 0;
        state.setupBilling.monthly.pro.seatPrice = plan.pro_seat_price || 0;
        state.setupBilling.monthly.pro.credits = plan.pro_credits_per_seat || 0;

        // Quarterly
        state.setupBilling.quarterly.starter.seatPrice =
          plan.starter_seat_quarterly_price || 0;
        state.setupBilling.quarterly.starter.credits =
          plan.starter_quarterly_credits_per_seat || 0;
        state.setupBilling.quarterly.growth.seatPrice =
          plan.human_seat_quarterly_price || 0;
        state.setupBilling.quarterly.growth.credits =
          plan.quarterly_software_credits_per_human_seat || 0;
        state.setupBilling.quarterly.pro.seatPrice =
          plan.pro_seat_quarterly_price || 0;
        state.setupBilling.quarterly.pro.credits =
          plan.pro_quarterly_credits_per_seat || 0;

        // Semi-Annually
        state.setupBilling.semi_annual.starter.seatPrice =
          plan.starter_seat_semi_annual_price || 0;
        state.setupBilling.semi_annual.starter.credits =
          plan.starter_semi_annual_credits_per_seat || 0;
        state.setupBilling.semi_annual.growth.seatPrice =
          plan.human_seat_semi_annual_price || 0;
        state.setupBilling.semi_annual.growth.credits =
          plan.semi_annual_software_credits_per_human_seat || 0;
        state.setupBilling.semi_annual.pro.seatPrice =
          plan.pro_seat_semi_annual_price || 0;
        state.setupBilling.semi_annual.pro.credits =
          plan.pro_semi_annual_credits_per_seat || 0;

        // Annually
        state.setupBilling.annual.starter.seatPrice =
          plan.starter_seat_annual_price || 0;
        state.setupBilling.annual.starter.credits =
          plan.starter_annual_credits_per_seat || 0;
        state.setupBilling.annual.growth.seatPrice =
          plan.human_seat_annual_price || 0;
        state.setupBilling.annual.growth.credits =
          plan.annual_software_credits_per_human_seat || 0;
        state.setupBilling.annual.pro.seatPrice =
          plan.pro_seat_annual_price || 0;
        state.setupBilling.annual.pro.credits =
          plan.pro_annual_credits_per_seat || 0;

        // Two Year
        state.setupBilling.two_year.starter.seatPrice =
          plan.starter_seat_two_year_price || 0;
        state.setupBilling.two_year.starter.credits =
          plan.starter_two_year_credits_per_seat || 0;
        state.setupBilling.two_year.growth.seatPrice =
          plan.human_seat_two_year_price || 0;
        state.setupBilling.two_year.growth.credits =
          plan.two_year_software_credits_per_human_seat || 0;
        state.setupBilling.two_year.pro.seatPrice =
          plan.pro_seat_two_year_price || 0;
        state.setupBilling.two_year.pro.credits =
          plan.pro_two_year_credits_per_seat || 0;

        state.setupBilling.trialDays = plan.trial_days || 0;
        state.setupBilling.freePlanCredits = plan.free_plan_credits || 0;
        state.setupBilling.currentPlanId = plan.id || null;
      }
    },

    SET_TRIAL_DAYS(state, value) {
      state.setupBilling.trialDays = value;
    },

    SET_FREE_PLAN_CREDITS(state, value) {
      state.setupBilling.trialDays = value;
    },

    SET_BILLING_PACK(state, value) {
      state.setupBilling.billingPack = value;
    },

    SET_PAYMENT_DUE(state, data) {
      console.log("Setting payment due");
      state.paymentDue = data.paymentDue;
      state.retryLink = data.retryLink;
      state.overdueSince = data.overdueSince;
      state.billingEndDate = data.billingEndDate;
    },

    SET_ENABLED_BILLING_PERIODS(state, value) {
      state.enabledBillingPeriods = value;
    },

    SET_ENABLED_TIERS(state, value) {
      state.enabledTiers = value;
    },
  },

  actions: {
    async getBillingDetails({ dispatch, state }) {
      try {
        await dispatch("toFetchCurrentWorkspaceBillingVersion");
        await dispatch("toGetBillingAccount");
        // v4 has no seats — credits are purchased directly via the additional_credits wallet
        if (state.billingVersion === billingVersions.V4) {
          await dispatch("toGetV4Credits");
        } else {
          await dispatch("toGetTotalSeats");
        }
      } catch (error) {
        console.error(
          "An error occurred while getting billing details: ",
          error,
        );
      }
    },

    async toFetchCurrentWorkspaceBillingVersion({ commit }) {
      try {
        const response =
          await DashboardService.fetchCurrentWorkspaceBillingVersion();
        if (response?.success) {
          commit("SET_BILLING_VERSION", response?.billing_version);
          commit("SET_IS_V3", response?.billing_version === "v3");
          commit("SET_IS_V4", response?.billing_version === "v4");
          if (response?.billing_version === "v0") {
            commit("SET_IS_BILLING_SETUP_COMPLETE", true); // Billing v0 is always setup complete
          }
        }
      } catch (error) {
        console.error(
          "An error occurred while fetching current workspace billing version: ",
          error,
        );
      }
    },

    async toGetBillingAccount({ commit }) {
      try {
        const response = await BillingService.getBillingAccount();
        if (response?.success) {
          const data = response?.account;
          commit(
            "SET_HAS_CARD_ATTCHED",
            data?.default_payment_method !== "" &&
            data?.default_payment_method !== null,
          );
          commit("SET_USER_SEAT_ENABLED", data?.user_seat_enabled || false);
          commit("SET_HAS_PAID_PLAN", data?.has_paid_plan || false);
          commit("SET_ACCOUNT_STATUS", data?.status || "");
          if (response?.billing_version) {
            commit("SET_BILLING_VERSION", response.billing_version);
          }
          if (response?.billing_version === "v0") {
            commit("SET_IS_BILLING_SETUP_COMPLETE", true); // Billing v0 is always setup complete
          } else {
            commit(
              "SET_IS_BILLING_SETUP_COMPLETE",
              data
                ? data?.status.toLowerCase() !==
                billingAccountStatus.NEW.toLowerCase()
                : false,
            );
          }
          commit("SET_IS_V3", response?.billing_version === "v3");
          commit("SET_IS_V4", response?.billing_version === "v4");

          return data;
        }
      } catch (error) {
        console.error(
          "An error occurred while getting billing account: ",
          error,
        );
        return null;
      }
    },

    async toGetTotalSeats({ commit }) {
      try {
        const response = await BillingService.getTotalSeats();
        if (response?.success) {
          commit("SET_HUMAN_SEATS", response?.human_seats);
        }
      } catch (error) {
        console.error("An error occurred while getting total seats: ", error);
      }
    },

    setBillingVersion({ commit }, data) {
      commit("SET_BILLING_VERSION", data);
    },

    setIsV3({ commit }, data) {
      commit("SET_IS_V3", data);
    },

    setIsV4({ commit }, data) {
      commit("SET_IS_V4", data);
    },

    // Fetches wallet balance from the additional_credits table (v4 workspaces only)
    async toGetV4Credits({ commit }) {
      try {
        const response = await BillingServiceV4.getCredits();
        if (response?.success) {
          commit("SET_V4_CREDITS", {
            additional_credits: response.additional_credits,
            is_recurring: response.is_recurring,
          });
        }
      } catch (error) {
        console.error("An error occurred while getting v4 credits: ", error);
      }
    },

    setHasCardAttched({ commit }, data) {
      commit("SET_HAS_CARD_ATTCHED", data);
    },

    setHasPaidPlan({ commit }, data) {
      commit("SET_HAS_PAID_PLAN", data);
    },

    setHumanSeats({ commit }, data) {
      commit("SET_HUMAN_SEATS", data);
    },

    setAccountStatus({ commit }, data) {
      commit("SET_ACCOUNT_STATUS", data);
    },

    setSelectedPlan({ commit }, data) {
      commit("SET_SELECTED_PLAN", data);
    },

    setUserSeatEnabled({ commit }, data) {
      commit("SET_USER_SEAT_ENABLED", data);
    },

    setUserSeatCount({ commit }, data) {
      commit("SET_USER_SEAT_COUNT", data);
      toCalculateDue().then();
    },

    setIsBillingSetupComplete({ commit }, data) {
      commit("SET_IS_BILLING_SETUP_COMPLETE", data);
    },

    setPlans({ commit }, data) {
      commit("SET_PLANS", data);
    },

    setTrialDays({ commit }, data) {
      commit("SET_TRIAL_DAYS", data);
    },

    setFreePlanCredits({ commit }, data) {
      commit("SET_FREE_PLAN_CREDITS", data);
    },

    setBillingPack({ commit }, data) {
      commit("SET_BILLING_PACK", data);
    },

    setPaymentDue({ commit }, data) {
      commit("SET_PAYMENT_DUE", data);
    },

    async fetchEnabledBillingPeriods({ commit }) {
      try {
        const response = await BillingService.getEnabledBillingPeriods();
        if (response?.success && response?.enabled_periods) {
          commit("SET_ENABLED_BILLING_PERIODS", response.enabled_periods);
        }
      } catch (error) {
        console.error(
          "An error occurred while fetching enabled billing periods: ",
          error,
        );
      }
    },

    async fetchEnabledTiers({ commit }) {
      try {
        const response = await BillingService.getEnabledTiers();
        if (response?.success && response?.enabled_tiers) {
          commit("SET_ENABLED_TIERS", response.enabled_tiers);
        }
      } catch (error) {
        console.error(
          "An error occurred while fetching enabled tiers: ",
          error,
        );
      }
    },
  },

  getters: {
    billingVersion: (state) => state.billingVersion,
    hasPaidPlan: (state) => state.hasPaidPlan,
    humanSeats: (state) => state.humanSeats,
    status: (state) => state.status,
    setupBilling: (state) => state.setupBilling,
    userSeatEnabled: (state) => state.userSeatEnabled,
    userSeatCount: (state) => state.setupBilling.userSeatCount,
    isBillingSetupComplete: (state) =>
      process.env.NODE_ENV === "development"
        ? true
        : state.setupBilling.isBillingSetupComplete,
    selectedPlan: (state) => state.setupBilling.selectedPlan,
    trialDays: (state) => state.setupBilling.trialDays,
    freePlanCredits: (state) => state.setupBilling.freePlanCredits,
    billingPack: (state) => state.setupBilling.billingPack,
    currentPlanId: (state) => state.setupBilling.currentPlanId,
    userSeatCost: (state) => {
      const pack = state.setupBilling.billingPack;
      const plan = state.setupBilling.selectedPlan;
      return state.setupBilling[pack]?.[plan]?.seatPrice || 0;
    },
    userSeatCredits: (state) => {
      const pack = state.setupBilling.billingPack;
      const plan = state.setupBilling.selectedPlan;
      return state.setupBilling[pack]?.[plan]?.credits || 0;
    },
    totalBilled: (state) => {
      const pack = state.setupBilling.billingPack;
      const plan = state.setupBilling.selectedPlan;
      const seatPrice = state.setupBilling[pack]?.[plan]?.seatPrice || 0;
      return seatPrice * state.setupBilling.userSeatCount;
    },
    totalCredits: (state) => {
      const pack = state.setupBilling.billingPack;
      const plan = state.setupBilling.selectedPlan;
      const credits = state.setupBilling[pack]?.[plan]?.credits || 0;
      return credits * state.setupBilling.userSeatCount;
    },
    digitalSeatCost: () => 0,
    digitalSeatCount: () => 0,
    digitalSeatCredits: () => 0,
    starterPlan: (state) => {
      const pack = state.setupBilling.billingPack;
      const packData = state.setupBilling[pack]?.starter;

      if (!packData) return { seatPrice: 0, credits: 0 };

      // Calculate per-month price for display (rounded to 2 decimals)
      let divisor = 1;
      switch (pack) {
        case billingPacks.ANNUALLY:
          divisor = 12;
          break;
        case billingPacks.QUARTERLY:
          divisor = 3;
          break;
        case billingPacks.SEMI_ANNUALLY:
          divisor = 6;
          break;
        case billingPacks.TWO_YEARS:
          divisor = 24;
          break;
        default:
          divisor = 1;
      }

      return {
        ...packData,
        seatPrice: Math.round((packData.seatPrice / divisor) * 100) / 100,
      };
    },
    growthPlan: (state) => {
      const pack = state.setupBilling.billingPack;
      const packData = state.setupBilling[pack]?.growth;

      if (!packData) return { seatPrice: 0, credits: 0 };

      // Calculate per-month price for display (rounded to 2 decimals)
      let divisor = 1;
      switch (pack) {
        case billingPacks.ANNUALLY:
          divisor = 12;
          break;
        case billingPacks.QUARTERLY:
          divisor = 3;
          break;
        case billingPacks.SEMI_ANNUALLY:
          divisor = 6;
          break;
        case billingPacks.TWO_YEARS:
          divisor = 24;
          break;
        default:
          divisor = 1;
      }

      return {
        ...packData,
        seatPrice: Math.round((packData.seatPrice / divisor) * 100) / 100,
      };
    },
    proPlan: (state) => {
      const pack = state.setupBilling.billingPack;
      const packData = state.setupBilling[pack]?.pro;

      if (!packData) return { seatPrice: 0, credits: 0 };

      // Calculate per-month price for display (rounded to 2 decimals)
      let divisor = 1;
      switch (pack) {
        case billingPacks.ANNUALLY:
          divisor = 12;
          break;
        case billingPacks.QUARTERLY:
          divisor = 3;
          break;
        case billingPacks.SEMI_ANNUALLY:
          divisor = 6;
          break;
        case billingPacks.TWO_YEARS:
          divisor = 24;
          break;
        default:
          divisor = 1;
      }

      return {
        ...packData,
        seatPrice: Math.round((packData.seatPrice / divisor) * 100) / 100,
      };
    },
    // Monthly prices for showing original crossed-out price
    starterMonthlyPrice: (state) => {
      return state.setupBilling.monthly?.starter?.seatPrice || 0;
    },
    growthMonthlyPrice: (state) => {
      return state.setupBilling.monthly?.growth?.seatPrice || 0;
    },
    proMonthlyPrice: (state) => {
      return state.setupBilling.monthly?.pro?.seatPrice || 0;
    },
    paymentDue: (state) => state.paymentDue,
    paymentRetryLink: (state) => state.retryLink,
    paymentOverdueSince: (state) => state.overdueSince,
    billingEndDate: (state) => state.billingEndDate,
    enabledBillingPeriods: (state) => state.enabledBillingPeriods,
    enabledTiers: (state) => state.enabledTiers,
    isV3: (state) => state.isV3,
    isV4: (state) => state.isV4,
    hasCardAttched: (state) => state.hasCardAttched,
    // v4 wallet: { additionalCredits, isRecurring }
    v4Credits: (state) => state.v4,
  },
};
